import { useState, useEffect } from "react";
import { Modal } from "../../components/Modal";
import { Button, Field, Input } from "../../components/ui";
import { AlertCircle, CheckCircle, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/apiClient";

export default function ChangePasswordModal({ open, onClose, onSuccess }) {
  const { partner } = useAuth();
  const [step, setStep] = useState(1); // 1: request OTP, 2: verify OTP + new password
  const [username, setUsername] = useState(partner?.username || "");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [expiresIn, setExpiresIn] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Handle cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  useEffect(() => {
    if (expiresIn <= 0) return;
    const t = setInterval(() => setExpiresIn((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [expiresIn]);

  // Password strength checker
  useEffect(() => {
    let strength = 0;
    if (newPassword.length > 0) {
      if (newPassword.length >= 8) strength++;
      if (/[a-z]/.test(newPassword) && /[A-Z]/.test(newPassword)) strength++;
      if (/\d/.test(newPassword)) strength++;
      if (/[^a-zA-Z0-9]/.test(newPassword)) strength++;
    }
    setPasswordStrength(strength);
  }, [newPassword]);

  // Request OTP for password change
  async function requestOtp(e) {
    e?.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await api.post("/auth/forgot-password/request-otp", {
        username: username.trim()
      });
      setExpiresIn(response.data.data.expiresInSeconds || 120);
      setCooldown(30);
      setStep(2);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.errors?.[0]?.message ||
          "Could not send OTP. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }

  // Verify OTP and change password
  async function verifyAndChange(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    
    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }

    // Validate password strength
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long.");
      setLoading(false);
      return;
    }

    try {
      const response = await api.post("/auth/forgot-password/verify-otp", {
        username: username.trim(),
        otp: otp.trim(),
        newPassword: newPassword
      });

      setSuccess(true);
      setTimeout(() => {
        onSuccess?.();
        onClose();
        resetForm();
      }, 2000);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.errors?.[0]?.message ||
          "Invalid OTP or password doesn't meet requirements."
      );
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setStep(1);
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    setSuccess(false);
    setLoading(false);
    setCooldown(0);
    setExpiresIn(0);
    setPasswordStrength(0);
  }

  function handleClose() {
    resetForm();
    onClose();
  }

  function formatTime(s) {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r.toString().padStart(2, "0")}`;
  }

  const getStrengthColor = () => {
    if (newPassword.length === 0) return "bg-gray-200";
    if (passwordStrength <= 1) return "bg-red-500";
    if (passwordStrength === 2) return "bg-orange-500";
    if (passwordStrength === 3) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getStrengthText = () => {
    if (newPassword.length === 0) return "";
    if (passwordStrength <= 1) return "Weak";
    if (passwordStrength === 2) return "Fair";
    if (passwordStrength === 3) return "Good";
    return "Strong";
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Change Password"
      description="Verify your identity and set a new password"
      className="bg-white/95 backdrop-blur-sm border border-emerald-100 shadow-2xl shadow-emerald-200/50"
    >
      {success ? (
        <div className="text-center py-6">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
            <CheckCircle size={32} className="text-emerald-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-800">Password Updated!</h3>
          <p className="text-sm text-gray-500 mt-1">
            Your password has been changed successfully.
          </p>
          <p className="text-xs text-gray-400 mt-2">
            All other sessions have been signed out for your security.
          </p>
        </div>
      ) : (
        <form onSubmit={step === 1 ? requestOtp : verifyAndChange}>
          {error && (
            <div className="mb-4 flex items-start gap-3 rounded-xl bg-red-50 border border-red-200 px-4 py-3">
              <AlertCircle size={18} className="mt-0.5 shrink-0 text-red-500" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-xs text-red-600">{error}</p>
              </div>
            </div>
          )}

          {step === 1 ? (
            <>
              <Field 
                label="Username" 
                required 
                hint="Enter your partner username"
              >
                <Input
                  autoFocus
                  placeholder="ALF4821"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl transition-all duration-200"
                />
              </Field>

              <div className="mt-4">
                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25 transition-all duration-200" 
                  loading={loading}
                >
                  Send OTP
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                <p className="text-sm text-gray-600">
                  OTP sent to registered phone for <span className="font-semibold text-gray-800">{username}</span>
                  {expiresIn > 0 && (
                    <span className="ml-2 text-emerald-600 font-medium">
                      · expires in {formatTime(expiresIn)}
                    </span>
                  )}
                </p>
              </div>

              <Field label="OTP Code" required>
                <Input
                  autoFocus
                  placeholder="Enter 6-digit code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  inputMode="numeric"
                  maxLength={8}
                  required
                  className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl transition-all duration-200"
                />
              </Field>

              <Field label="New Password" >
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    minLength={6}
                    maxLength={128}
                    required
                    className="pr-11 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl transition-all duration-200"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </Field>

           
              <Field label="Confirm Password" required>
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="pr-11 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl transition-all duration-200"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </Field>

              <div className="mt-4 flex gap-2">
                <Button 
                  type="button" 
                  variant="ghost" 
                  onClick={handleClose}
                  className="flex-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25 transition-all duration-200" 
                  loading={loading}
                >
                  Change Password
                </Button>
              </div>

              <button
                type="button"
                disabled={cooldown > 0}
                onClick={requestOtp}
                className={`mt-3 w-full text-center text-sm font-medium transition-colors ${
                  cooldown > 0 
                    ? 'text-gray-400 cursor-not-allowed' 
                    : 'text-emerald-600 hover:text-emerald-700 hover:underline'
                }`}
              >
                {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend OTP"}
              </button>
            </>
          )}
        </form>
      )}
    </Modal>
  );
}